import React, { useEffect, useState, useMemo } from "react";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { api } from "@/api";
import { toast } from "@/hooks/use-toast";
import { formatError } from "@/components/shared/helpers";

// Mappage catégorie → style (couleur de la puce et du badge)
const CATEGORY_META = {
  stay:        { label: "Séjour",       dot: "bg-sky-400",      ring: "ring-sky-400/30",      tag: "bg-sky-500/15 text-sky-200" },
  invoice:     { label: "Facture",      dot: "bg-violet-400",   ring: "ring-violet-400/30",   tag: "bg-violet-500/15 text-violet-200" },
  request:     { label: "Demande",      dot: "bg-orange-400",   ring: "ring-orange-400/30",   tag: "bg-orange-500/15 text-orange-200" },
  supplement:  { label: "Surcoût",      dot: "bg-pink-400",     ring: "ring-pink-400/30",     tag: "bg-pink-500/15 text-pink-200" },
  manual_line: { label: "Ligne manuelle", dot: "bg-amber-400",  ring: "ring-amber-400/30",    tag: "bg-amber-500/15 text-amber-200" },
  entity:      { label: "Fiche",        dot: "bg-emerald-400",  ring: "ring-emerald-400/30",  tag: "bg-emerald-500/15 text-emerald-200" },
  ical:        { label: "iCal",         dot: "bg-cyan-400",     ring: "ring-cyan-400/30",     tag: "bg-cyan-500/15 text-cyan-200" },
  login:       { label: "Connexion",    dot: "bg-slate-400",    ring: "ring-slate-400/30",    tag: "bg-slate-500/15 text-slate-200" },
  other:       { label: "Autre",        dot: "bg-zinc-500",     ring: "ring-zinc-500/30",     tag: "bg-zinc-500/15 text-zinc-200" },
};

const FIELD_FR = {
  start_date: "Date d'arrivée", end_date: "Date de départ", tenant_name: "Locataire",
  tenant_email: "Email locataire", services: "Prestations", status: "Statut",
  paid: "Soldée", amount_ttc: "Montant TTC", invoice_number: "N° facture",
  notes: "Notes", first_name: "Prénom", last_name: "Nom", phone: "Téléphone",
  billing_email: "Email facturation", billing_address: "Adresse facturation",
  short_name: "Nom court", address: "Adresse", keybox_code: "Keybox",
  time_slot: "Créneau", cleaning_minutes_est: "Temps ménage", pricing: "Tarifs",
};

function formatParisDate(iso) {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    if (isNaN(d.getTime())) return iso.slice(0, 16).replace("T", " ");
    return d.toLocaleString("fr-FR", { timeZone: "Europe/Paris", dateStyle: "short", timeStyle: "short" });
  } catch { return iso; }
}

function formatDayHeader(iso) {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    return d.toLocaleDateString("fr-FR", { timeZone: "Europe/Paris", weekday: "long", day: "2-digit", month: "long", year: "numeric" });
  } catch { return iso; }
}

function formatTimeOnly(iso) {
  try {
    const d = new Date(iso);
    return d.toLocaleTimeString("fr-FR", { timeZone: "Europe/Paris", hour: "2-digit", minute: "2-digit" });
  } catch { return ""; }
}

function buildEventSummary(ev) {
  const d = ev.details || {};
  const bits = [];
  if (d.stay_start_date || d.stay_end_date) bits.push(`Séjour ${d.stay_start_date || "?"} → ${d.stay_end_date || "?"}`);
  if (d.tenant_name) bits.push(`Locataire : ${d.tenant_name}`);
  if (d.invoice_number) bits.push(`N° ${d.invoice_number}`);
  if (typeof d.amount_ttc === "number") bits.push(`${d.amount_ttc.toFixed(2)} € TTC`);
  if (d.description) bits.push(d.description);
  if (d.reason) bits.push(`Motif : ${d.reason}`);
  if (Array.isArray(d.fields) && d.fields.length) bits.push("Champs modifiés : " + d.fields.map((f) => FIELD_FR[f] || f).join(", "));
  if (Array.isArray(d.changes) && d.changes.length && typeof d.changes[0] === "string") bits.push("Modifié : " + d.changes.map((c) => FIELD_FR[c] || c).join(", "));
  if (d.changes && typeof d.changes === "object" && !Array.isArray(d.changes)) {
    const ks = Object.keys(d.changes).slice(0, 4).map((k) => FIELD_FR[k] || k);
    if (ks.length) bits.push("Modifié : " + ks.join(", "));
  }
  if (d.platform) bits.push(`Plateforme : ${d.platform}`);
  if (d.ical_count !== undefined) bits.push(`${d.ical_count} séjour(s)`);
  return bits.join(" · ");
}

export default function AdminOwnerTimeline({ open, onOpenChange, ownerId, ownerLabel }) {
  const [loading, setLoading] = useState(false);
  const [events, setEvents] = useState([]);
  const [ownerDisplay, setOwnerDisplay] = useState(ownerLabel || "");
  const [filterCat, setFilterCat] = useState("all");

  useEffect(() => {
    if (!open || !ownerId) return;
    let alive = true;
    setLoading(true);
    api.get(`/admin/owners/${ownerId}/timeline?limit=500`)
      .then((res) => {
        if (!alive) return;
        setEvents(res.data?.events || []);
        setOwnerDisplay(res.data?.owner_display || ownerLabel || "");
      })
      .catch((e) => toast({ title: "Erreur chargement timeline", description: formatError(e), variant: "destructive" }))
      .finally(() => alive && setLoading(false));
    return () => { alive = false; };
  }, [open, ownerId, ownerLabel]);

  const filtered = useMemo(() => (filterCat === "all" ? events : events.filter((e) => e.category === filterCat)), [events, filterCat]);

  // Regrouper par jour
  const groupedByDay = useMemo(() => {
    const groups = [];
    let currentKey = null;
    let currentGroup = null;
    for (const ev of filtered) {
      const key = (ev.timestamp_paris || ev.timestamp || "").slice(0, 10);
      if (key !== currentKey) {
        currentKey = key;
        currentGroup = { key, events: [] };
        groups.push(currentGroup);
      }
      currentGroup.events.push(ev);
    }
    return groups;
  }, [filtered]);

  const counts = useMemo(() => {
    const c = { all: events.length };
    for (const e of events) c[e.category] = (c[e.category] || 0) + 1;
    return c;
  }, [events]);

  const exportPdf = async () => {
    try {
      const res = await api.get(`/admin/owners/${ownerId}/timeline.pdf`, { responseType: "blob" });
      const blob = new Blob([res.data], { type: "application/pdf" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `timeline_${(ownerDisplay || "proprietaire").replace(/\s+/g, "_")}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 1000);
    } catch (e) {
      toast({ title: "Erreur export PDF", description: formatError(e), variant: "destructive" });
    }
  };

  const availableCats = useMemo(() => {
    const set = new Set(events.map((e) => e.category));
    return Array.from(set);
  }, [events]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        data-testid="owner-timeline-dialog"
        className="border-white/10 bg-[#0f1320] text-slate-100 max-w-3xl max-h-[90vh] overflow-hidden flex flex-col"
      >
        <DialogHeader className="space-y-2">
          <DialogTitle data-testid="owner-timeline-title" className="text-slate-100">
            Timeline — {ownerDisplay || "Propriétaire"}
          </DialogTitle>
          <DialogDescription className="sr-only">
            Fil d&apos;activité antéchronologique de tous les événements liés à ce propriétaire (séjours, factures, demandes, surcoûts, connexions). Filtrable par catégorie et exportable en PDF.
          </DialogDescription>
          <div className="flex flex-wrap items-center gap-2 text-xs text-slate-300">
            <span data-testid="owner-timeline-count">
              {loading ? "Chargement…" : `${filtered.length} événement(s) affiché(s) / ${events.length} total`}
            </span>
            <div className="ml-auto flex gap-2">
              <Button
                data-testid="owner-timeline-export-pdf"
                size="sm"
                className="rounded-full bg-violet-500 text-white hover:bg-violet-400"
                onClick={exportPdf}
                disabled={loading || !events.length}
              >
                ⬇️ Exporter en PDF
              </Button>
            </div>
          </div>
          {/* Filtres catégorie */}
          <div className="flex flex-wrap gap-1 pt-1">
            <button
              data-testid="owner-timeline-filter-all"
              onClick={() => setFilterCat("all")}
              className={`rounded-full px-3 py-1 text-xs transition ${filterCat === "all" ? "bg-white/15 text-white ring-1 ring-white/20" : "bg-white/[0.05] text-slate-300 hover:bg-white/10"}`}
            >
              Tous ({counts.all || 0})
            </button>
            {availableCats.map((cat) => {
              const meta = CATEGORY_META[cat] || CATEGORY_META.other;
              const active = filterCat === cat;
              return (
                <button
                  key={cat}
                  data-testid={`owner-timeline-filter-${cat}`}
                  onClick={() => setFilterCat(cat)}
                  className={`rounded-full px-3 py-1 text-xs transition ${active ? `${meta.tag} ring-1 ${meta.ring}` : "bg-white/[0.05] text-slate-300 hover:bg-white/10"}`}
                >
                  <span className={`mr-1 inline-block h-2 w-2 rounded-full ${meta.dot}`} />
                  {meta.label} ({counts[cat] || 0})
                </button>
              );
            })}
          </div>
        </DialogHeader>

        <div className="flex-1 overflow-y-auto -mx-6 px-6">
          {loading && (
            <div data-testid="owner-timeline-loading" className="py-10 text-center text-slate-300">Chargement…</div>
          )}
          {!loading && !filtered.length && (
            <div data-testid="owner-timeline-empty" className="py-10 text-center text-slate-300">
              Aucun événement enregistré.
            </div>
          )}
          {!loading && groupedByDay.map((group) => (
            <div key={group.key} className="mb-4">
              <div data-testid={`owner-timeline-day-${group.key}`} className="sticky top-0 z-10 -mx-6 mb-2 bg-[#0f1320]/95 px-6 py-2 text-xs font-semibold uppercase tracking-wide text-slate-300 backdrop-blur">
                {formatDayHeader(group.events[0].timestamp_paris || group.events[0].timestamp)}
              </div>
              <ol className="relative ml-2 border-l border-white/10 pl-5">
                {group.events.map((ev, idx) => {
                  const meta = CATEGORY_META[ev.category] || CATEGORY_META.other;
                  const summary = buildEventSummary(ev);
                  return (
                    <li
                      key={ev.id || `${ev.timestamp}-${idx}`}
                      data-testid={`owner-timeline-event-${ev.id || idx}`}
                      className="relative mb-3 ml-1 last:mb-0"
                    >
                      <span className={`absolute -left-[1.6rem] top-1.5 h-3 w-3 rounded-full ${meta.dot} ring-4 ${meta.ring}`} />
                      <div className="rounded-xl border border-white/10 bg-white/[0.04] p-3 hover:bg-white/[0.07] transition">
                        <div className="flex items-start justify-between gap-2">
                          <div className="min-w-0 flex-1">
                            <div className="flex flex-wrap items-center gap-2">
                              <span className="text-sm font-medium text-slate-50">{ev.action_label}</span>
                              <span className={`rounded-full px-2 py-0.5 text-[10px] uppercase tracking-wide ${meta.tag}`}>{meta.label}</span>
                              {ev.actor_role === "owner" && (
                                <span className="rounded-full bg-blue-500/15 px-2 py-0.5 text-[10px] text-blue-200">Propriétaire</span>
                              )}
                              {ev.actor_role === "admin" && (
                                <span className="rounded-full bg-fuchsia-500/15 px-2 py-0.5 text-[10px] text-fuchsia-200">Admin</span>
                              )}
                            </div>
                            {ev.property_short_name && (
                              <div className="mt-1 text-xs text-slate-300">
                                <span className="text-slate-400">Logement :</span> {ev.property_short_name}
                              </div>
                            )}
                            {summary && (
                              <div className="mt-1 text-xs text-slate-300">{summary}</div>
                            )}
                          </div>
                          <div className="shrink-0 text-right">
                            <div className="text-xs font-medium text-slate-200">{formatTimeOnly(ev.timestamp_paris || ev.timestamp)}</div>
                            <div className="text-[10px] text-slate-400">{formatParisDate(ev.timestamp_paris || ev.timestamp).split(" ")[0]}</div>
                          </div>
                        </div>
                      </div>
                    </li>
                  );
                })}
              </ol>
            </div>
          ))}
        </div>
      </DialogContent>
    </Dialog>
  );
}
